import LogRocket from 'logrocket';

import {
  TELEMETRY_EVENTS,
  type TelemetryEventName,
  type TelemetryPayload,
  type TelemetryPayloads
} from '../config/telemetry-events';
import { sanitizeNetworkEntry } from '../util/logrocket-sanitizers';
import { getScopeRole } from './current-context';
import { getEntityScope } from './entity-scope-read';

/**
 * The one module that talks to LogRocket; an ESLint rule keeps every other
 * production file from importing it. Events are declared in
 * `config/telemetry-events.ts` and sent with `track()`; `breadcrumb()` adds a
 * note to the session replay's console feed without declaring anything.
 *
 * LogRocket drops calls made before `init`, and init waits for
 * `AuthenticatedShell` to mount, so calls made earlier on the same page are
 * held and sent once `startTelemetry()` has run. A build that never records
 * (local dev, a `dev` origin, Vitest) passes calls straight through to the
 * uninitialised SDK, which ignores them, and mirrors events to the console
 * outside tests.
 *
 * Nothing here may throw into its caller: telemetry runs inside request
 * header building, render effects and unload handlers.
 */

/** Whether this build records sessions: the QA and prod deploys only. */
export const telemetryEnabled = (): boolean =>
  import.meta.env.PROD && !window.origin.includes('dev');

// Bounded: a page that records but never initialises (the public gallery
// mounts no AuthenticatedShell) must not grow it forever.
const MAX_HELD_CALLS = 100;

let ready = false;
let held: Array<() => void> = [];
const sentOnce = new Set<string>();

/** Runs one SDK call, and reports whether it went through. */
const attempt = (call: () => void): boolean => {
  try {
    call();
    return true;
  } catch {
    // Telemetry must never take its caller down with it.
    return false;
  }
};

/** Whether the call was made, or held for init; false when it was dropped. */
const send = (call: () => void): boolean => {
  if (ready || !telemetryEnabled()) return attempt(call);
  if (held.length >= MAX_HELD_CALLS) return false;
  held.push(call);
  return true;
};

/**
 * Starts session recording, in a build that records. Called once by
 * `AuthenticatedShell`; a second call does nothing.
 */
export const startTelemetry = (): void => {
  if (ready || !telemetryEnabled()) return;
  attempt(initAndIdentify);
  // Even when init threw: held calls then reach an idle SDK that ignores them,
  // rather than being held for an init that will not come.
  markTelemetryReady();
};

const initAndIdentify = (): void => {
  // `release` tags every session with the deployed build (git short SHA), so a
  // session can be correlated to a specific bundle. package.json's version is
  // not kept in sync with release tags, so it can't serve here.
  // DV-2789 (PCI-DSS 3.3.3): strip the card security code (CVV) from captured
  // request/response bodies so it never lands in a session replay (LogRocket
  // records bodies by default).
  LogRocket.init('gx8tty/planomatic-prod', {
    release: __APP_VERSION__,
    network: {
      requestSanitizer: sanitizeNetworkEntry,
      responseSanitizer: sanitizeNetworkEntry
    }
  });

  // `name` and `email` are LogRocket's own traits: the session list shows them
  // and its search finds a session by either. All three are written at login
  // (CallBack and processTokenData).
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  const userEmail = localStorage.getItem('userEmail');
  if (userId && userName) {
    LogRocket.identify(userId, { name: userName, ...(userEmail ? { email: userEmail } : {}) });
  }
};

/** Sends what was held for init. `startTelemetry` calls it; tests call it directly. */
export const markTelemetryReady = (): void => {
  ready = true;
  const calls = held;
  held = [];
  calls.forEach(attempt);
};

/** Whether LogRocket has been initialised on this page. */
export const isTelemetryReady = (): boolean => ready;

/**
 * Sends a declared event, with the role and account this tab is looking at.
 * Paused events and repeats of a `onceKey` are dropped here. Returns whether
 * the event was sent, or held for init: false when it was paused, a repeat,
 * past the held cap, or LogRocket threw.
 */
export const track = <N extends TelemetryEventName>(
  name: N,
  payload: TelemetryPayloads[N]
): boolean => {
  try {
    const event = TELEMETRY_EVENTS[name];
    if (!event.enabled) return false;
    const onceKey = event.onceKey ? `${name}|${event.onceKey(payload)}` : null;
    if (onceKey && sentOnce.has(onceKey)) return false;
    const properties: TelemetryPayload = { ...telemetryViewerContext(), ...payload };
    mirrorToConsole(name, properties);
    const delivered = send(() => LogRocket.track(name, properties));
    // Only once it went out: a dropped call must not suppress a later one.
    if (delivered && onceKey) sentOnce.add(onceKey);
    return delivered;
  } catch {
    // Telemetry must never take its caller down with it.
    return false;
  }
};

/**
 * A note in the session replay's console feed, at the moment it happened. Not
 * filterable across sessions, so there is nothing to declare or retire. Not
 * held for init either: breadcrumbs can fire on every render (a permission
 * denial does), and held ones would crowd out the events.
 */
export const breadcrumb = (message: string, data: TelemetryPayload): void => {
  try {
    LogRocket.log(message, data);
  } catch {
    // Telemetry must never take its caller down with it.
  }
};

/**
 * The role and account this tab is looking at, which `track()` adds to every
 * event. Exported for an event captured now and sent later, which records it
 * at capture so the send does not stamp a later scope (access-denial-log).
 */
export const telemetryViewerContext = (): TelemetryPayload => {
  const scope = getEntityScope();
  return {
    scopeRole: getScopeRole() ?? '',
    scopeEntityId: (scope.status === 'scoped' && scope.entityId) || ''
  };
};

// LogRocket does not initialise in local dev, so this is the only place an
// event shows there.
const mirrorToConsole = (name: string, properties: TelemetryPayload): void => {
  if (telemetryEnabled() || import.meta.env.MODE === 'test') return;
  console.info(`[telemetry] ${name}`, properties);
};

/** Test-only: back to a fresh page load. Production code must never call this. */
export const __resetTelemetryForTests = (): void => {
  ready = false;
  held = [];
  sentOnce.clear();
};
